import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

const TYPE_LABELS: Record<string, string> = { similar_crops: 'Similar', outlier_crops: 'Outliers', should_be_carton: 'Carton', should_be_non_front: 'Non-front', clear_pending: 'Pending', assign_design: 'Assign', anything_else: 'Anything else?' };

const TYPE_DESIGN_INFO: Record<string, { what: string; why: string; generation: string; resolution: string; edge_cases: string }> = {
  similar_crops: {
    what: 'Highlights multiple crops across different designs that are visually similar to each other. Both the "misplaced" crops and the "correct" crops they resemble are highlighted together.',
    why: 'The most impactful cleanup action — wrong design assignments directly degrade embedding quality. Showing both sides (misplaced + correct) gives the labeler full context to decide: are these truly the same product in different designs (move them), or are they genuinely different products that happen to look alike (confirm separate)? Prioritized first because similar-looking crops in wrong designs create the strongest false positive signal in training data.',
    generation: 'At task generation time, the system computes pairwise cosine similarity between crop embeddings across designs within the same UPC. Crops exceeding the similarity threshold (e.g. 0.92) are grouped. Crops in Pending or Unassigned sections are excluded — they\'re already flagged for manual review via section-level highlights. The threshold is tuned per-chain to account for visual similarity differences between product categories.',
    resolution: 'The labeler clicks crops to select them, then clicks a design section header to move them. No auto-action — the labeler has full control. The highlight auto-completes (marks as resolved) when all highlighted crops have been moved or the designs are confirmed as correct. If a crop is moved out of view (different UPC), the highlight goes stale.',
    edge_cases: 'Cross-UPC similarity is not checked (different UPCs = different products by definition). If a crop appears in both similar_crops and outlier_crops, similar_crops takes precedence (outlier_crops excludes similar_crops IDs). Pending/unassigned crops are never included — they have their own section-level highlights.',
  },
  outlier_crops: {
    what: 'Highlights all crops that are embedding distance outliers within their respective designs — they don\'t visually match the other crops in the same design. All outliers across the task\'s UPCs are grouped into a single highlight.',
    why: 'Complements similar_crops: while similar_crops catches "wrong design" by finding cross-design matches, outlier_crops catches "wrong design" by finding within-design mismatches. A crop that\'s an outlier in its design might belong in a completely different design, or might be trash/non-front. Second priority because the damage is less targeted — an outlier might just be a bad crop rather than a training data poison.',
    generation: 'For each design, compute the centroid embedding and measure each crop\'s distance to it. Crops beyond a distance threshold are flagged as outliers. Crops already included in a similar_crops highlight are excluded to avoid double-highlighting. The threshold is relative to the design\'s internal spread — tighter clusters have stricter outlier detection.',
    resolution: 'The labeler selects the outlier crops and moves them to the correct design, or deactivates them if they\'re trash. No auto-action. The highlight resolves when all highlighted crops have been moved or confirmed.',
    edge_cases: 'Small designs (1-2 crops) can\'t meaningfully detect outliers — they\'re skipped. If a design has only outliers (all crops are far from centroid), it suggests the design itself is corrupt — but this is handled at a higher level (design health checks), not here.',
  },
  should_be_non_front: {
    what: 'Highlights a single crop that the classifier believes is a side or back view of the product, currently placed in the front-orientation section.',
    why: 'Front crops are used for the primary product recognition embedding. Non-front crops (side, back, angled) introduce noise into the embedding space because they show different visual features (nutrition labels, barcodes, different artwork). Separating orientations keeps embeddings clean. Lower priority than design misassignment because a non-front crop in the front section adds noise but doesn\'t create false cross-product matches.',
    generation: 'An orientation classifier (trained on labeled front/back/side data) scores each front-assigned crop. Crops classified as non-front with confidence above threshold are flagged. The classifier uses the full crop image, not just embeddings.',
    resolution: 'Accept moves the crop to the Non-front section of the same UPC. The crop is re-oriented in the database. The design auto-adjusts (picks a new representative if this was the representative). Skip confirms the crop belongs in front despite the classifier\'s prediction.',
    edge_cases: 'Some products look the same from all angles (cylindrical cans). The classifier may flag these — the labeler confirms. If moving the crop empties the design, the design is deactivated.',
  },
  should_be_carton: {
    what: 'Highlights a single crop that appears to be a carton/multi-pack rather than an individual product unit.',
    why: 'Cartons (24-packs, 6-packs) have different visual features than individual units — they show multiple product faces, box edges, shrink-wrap textures. Mixing cartons with singles in the same design degrades embedding quality because the model can\'t learn a consistent product appearance. Cartons get their own section and potentially their own design matching rules.',
    generation: 'A carton classifier (object detection + aspect ratio heuristics) scores each front-assigned crop. Crops classified as carton with high confidence are flagged. The classifier looks for multi-unit patterns, box-like shapes, and shrink-wrap textures.',
    resolution: 'Accept moves the crop to the Carton section of the same UPC. Skip confirms it\'s a single unit. Moving to Carton changes the crop\'s category tag in the database.',
    edge_cases: 'Display boxes (single unit in a cardboard display) can look like cartons. Some products are only sold as multi-packs and should remain in front. The labeler has product knowledge the classifier lacks.',
  },
  clear_pending: {
    what: 'Section-level highlight covering all pending designs across all UPCs in the task.',
    why: 'Pending designs are in a limbo state: the system created them but hasn\'t verified they\'re correct. Until a human confirms the barcode is right and the crops belong together, these designs shouldn\'t influence the embedding space.',
    generation: 'At task generation time, the system checks if any designs have barcodeStatus=pending. If so, a single clear_pending highlight is added.',
    resolution: 'No action buttons — the labeler works freeform. The highlight auto-completes when no pending designs remain across all UPCs.',
    edge_cases: 'If the labeler moves crops from a pending design to Unassigned (destroying the design), this highlight may auto-complete while assign_design gains more work.',
  },
  assign_design: {
    what: 'Section-level highlight covering all unassigned front crops across all UPCs in the task.',
    why: 'Unassigned crops are invisible to the embedding training pipeline. Every unassigned crop is a missed training signal.',
    generation: 'At task generation time, the system checks if any front-orientation crops have no design_id. If so, a single assign_design highlight is added.',
    resolution: 'The labeler selects unassigned crops and moves them to design sections (or creates new designs). The highlight auto-completes when all Unassigned sections are empty.',
    edge_cases: 'Some unassigned crops may actually be trash. The labeler can deactivate them — this also counts toward completion.',
  },
  anything_else: {
    what: 'A freeform step with no specific highlights. The labeler reviews the full task one final time.',
    why: 'Automated highlight generation can\'t catch everything. This step gives the labeler agency to apply their judgment.',
    generation: 'Always added as the last highlight in every Design Review task.',
    resolution: 'The labeler performs any freeform corrections or confirms everything looks good. No auto-completion — move past with the arrow.',
    edge_cases: 'The labeler may find nothing to fix, in which case they simply navigate past this step.',
  },
};

function InfoSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--purple)',
        marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{ color: 'var(--text-secondary)' }}>{children}</div>
    </div>
  );
}

interface TaskGuideOverlayProps {
  onClose: () => void;
}

export const TaskGuideOverlay: React.FC<TaskGuideOverlayProps> = ({ onClose }) => {
  const [tab, setTab] = useState<string>('overview');

  return (
    <div
      style={{
        position: 'absolute', inset: 0, zIndex: 500,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '90%', maxWidth: 720, maxHeight: '85%',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '14px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
            Design Review — Task Guide
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 2,
              color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center',
            }}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
        </div>

        <div style={{
          display: 'flex', gap: 0, padding: '10px 20px 0',
          borderBottom: '1px solid var(--border)', flexShrink: 0,
          overflowX: 'auto',
        }}>
          {[
            { key: 'overview', label: 'Overview' },
            ...Object.keys(TYPE_DESIGN_INFO).map(k => ({ key: k, label: TYPE_LABELS[k] || k })),
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '6px 14px', fontFamily: 'var(--font-mono)', fontSize: 10,
                fontWeight: tab === t.key ? 700 : 500,
                color: tab === t.key ? 'var(--purple)' : 'var(--text-tertiary)',
                borderBottom: tab === t.key ? '2px solid var(--purple)' : '2px solid transparent',
                transition: 'all 150ms',
                whiteSpace: 'nowrap',
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{
          flex: 1, overflowY: 'auto', padding: '16px 20px',
          fontFamily: 'var(--font-mono)', fontSize: 11, lineHeight: 1.65,
          color: 'var(--text-secondary)',
        }}>
          {tab === 'overview' ? (
            <div>
              <InfoSection label="Purpose">
                Design Review is the primary quality assurance task for the product recognition embedding pipeline.
                A labeler reviews 2–4 related UPCs displayed as side-by-side columns, each showing all designs,
                unassigned crops, and special sections (Pending, Non-front, Carton). The system pre-computes
                highlights — areas that likely need attention — and the labeler works through them while also
                performing freeform corrections.
              </InfoSection>
              <InfoSection label="Layout">
                Each UPC column displays sections top-to-bottom: <b>Pending</b>, <b>Unassigned</b>, <b>Designs</b>,
                <b>Carton</b>, <b>Non-front</b>. Each design section shows a header with ID, barcode status badge,
                and crop count, followed by reference images (F = front, ★ = representative, B = back) and the crop grid.
              </InfoSection>
              <InfoSection label="Selecting & Moving Crops">
                Click any crop to select it (purple border). Click additional crops to multi-select. The bottom bar
                shows the selection count and available actions. Click any section header to move crops there.
                Press <b>ESC</b> to deselect all.
              </InfoSection>
              <InfoSection label="Merging Designs">
                Select all crops in a design, then click "Merge into…" in the action bar. The source design enters
                merge mode (red border, SOURCE badge). Click another design to merge into it. Cancel with ESC.
              </InfoSection>
              <InfoSection label="Design Actions">
                <b>+ New Design</b> — creates a new design and moves selected crops into it.{' '}
                <b>Set Rep (★)</b> — marks a selected crop as the design's representative.{' '}
                <b>Deactivate</b> — soft-deletes selected crops.{' '}
                <b>Verify Barcode</b> — opens barcode verification for unconfirmed designs.
              </InfoSection>
              <InfoSection label="Highlights & Task Bar">
                Highlights are guides, not obligations. The bottom task bar shows progress pips, navigation arrows,
                the current highlight type, and summary. Priority: Similar → Outliers → Non-front → Carton →
                Pending → Assign → Anything else?. Navigate with arrow keys or clicking pips.
              </InfoSection>
              <InfoSection label="Zoom">
                Hold <b>Alt</b> and hover over any image to see a 240px zoom overlay near the cursor.
              </InfoSection>
              <InfoSection label="Undo">
                Most actions can be undone with the undo button or <b>Ctrl+Z</b>. The undo stack preserves
                the full state including highlight statuses.
              </InfoSection>
            </div>
          ) : TYPE_DESIGN_INFO[tab] ? (
            <div>
              {(() => {
                const info = TYPE_DESIGN_INFO[tab];
                return (
                  <>
                    <InfoSection label="What">{info.what}</InfoSection>
                    <InfoSection label="Why this priority">{info.why}</InfoSection>
                    <InfoSection label="How it's generated">{info.generation}</InfoSection>
                    <InfoSection label="How it's resolved">{info.resolution}</InfoSection>
                    <InfoSection label="Edge cases">{info.edge_cases}</InfoSection>
                  </>
                );
              })()}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const TYPE_COLORS: Record<string, string> = { similar_crops: 'var(--purple)', outlier_crops: 'var(--purple)', should_be_carton: 'var(--purple)', should_be_non_front: 'var(--purple)', clear_pending: 'var(--purple)', assign_design: 'var(--purple)', anything_else: 'var(--text-tertiary)' };

export { TYPE_LABELS, TYPE_COLORS };
